import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../Dialog/Dialog";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import SecondaryButton from "../SecondaryButton/SecondaryButton";
import { cn } from "../../lib/utils";
import { sanitizeEmailHtml } from "../../utils/emailHtmlSanitize";
import "./EmailHtmlDualEditor.css";

type Mode = "visual" | "html";

export type EmailHtmlDualEditorProps = {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  maxLength?: number;
  isDisabled?: boolean;
};

export function EmailHtmlDualEditor({
  value,
  onChange,
  label,
  maxLength = 100000,
  isDisabled = false,
}: EmailHtmlDualEditorProps) {
  const { t } = useTranslation();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const skipNextParentSync = useRef(false);
  /** Last sanitized HTML we pushed to the parent — avoids TipTap/DOMPurify mismatch loops. */
  const lastSyncedSanitizedRef = useRef<string>(sanitizeEmailHtml(value || ""));
  const [mode, setMode] = useState<Mode>("visual");
  const [htmlDraft, setHtmlDraft] = useState(value);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageUrlDraft, setImageUrlDraft] = useState("https://");
  const [imageAltDraft, setImageAltDraft] = useState("");
  const [imageUrlInvalid, setImageUrlInvalid] = useState(false);

  useEffect(() => {
    setHtmlDraft(value);
  }, [value]);

  const propagateSanitizedToParent = useCallback((rawHtml: string) => {
    const sanitized = sanitizeEmailHtml(rawHtml);
    skipNextParentSync.current = true;
    lastSyncedSanitizedRef.current = sanitized;
    onChangeRef.current(sanitized);
  }, []);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      TextAlign.configure({
        types: ["paragraph"],
        alignments: ["left", "center", "right", "justify"],
        defaultAlignment: "left",
      }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: sanitizeEmailHtml(value || "") || "<p></p>",
    editable: !isDisabled,
    onUpdate: ({ editor: ed }) => {
      propagateSanitizedToParent(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isDisabled);
  }, [editor, isDisabled]);

  useEffect(() => {
    if (!editor || mode !== "visual") return;
    if (skipNextParentSync.current) {
      skipNextParentSync.current = false;
      return;
    }
    const nextSan = sanitizeEmailHtml(value || "");
    const normalized = nextSan || "<p></p>";
    // Parent value matches what we last sent — do not setContent (prevents infinite sync loops).
    if (nextSan === lastSyncedSanitizedRef.current) {
      return;
    }
    const current = sanitizeEmailHtml(editor.getHTML());
    if (normalized === current) {
      lastSyncedSanitizedRef.current = nextSan;
      return;
    }
    editor.commands.setContent(normalized, false);
    lastSyncedSanitizedRef.current = nextSan;
  }, [value, editor, mode]);

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    if (next === "html" && editor) {
      propagateSanitizedToParent(editor.getHTML());
    }
    if (next === "visual") {
      propagateSanitizedToParent(htmlDraft);
    }
    setMode(next);
  };

  const onHtmlTextareaChange = (raw: string) => {
    setHtmlDraft(raw);
    propagateSanitizedToParent(raw);
  };

  const charCount = mode === "visual" ? sanitizeEmailHtml(editor?.getHTML() ?? "").length : htmlDraft.length;

  const addOrEditLink = () => {
    if (!editor) return;
    const prev = (editor.getAttributes("link").href as string | undefined) ?? "";
    const url = window.prompt(String(t("notifications.email_editor.link_prompt")), prev);
    if (url === null) return;
    const trimmed = url.trim();
    if (trimmed === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: trimmed }).run();
    }
  };

  const openImageModal = () => {
    if (!editor) return;
    setImageUrlDraft("https://");
    setImageAltDraft("");
    setImageUrlInvalid(false);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setImageUrlInvalid(false);
  };

  const insertImageFromModal = () => {
    if (!editor) return;
    const trimmed = imageUrlDraft.trim();
    if (!trimmed) {
      setImageUrlInvalid(true);
      return;
    }
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        setImageUrlInvalid(true);
        return;
      }
    } catch {
      setImageUrlInvalid(true);
      return;
    }
    setImageUrlInvalid(false);
    closeImageModal();
    editor.chain().focus().setImage({ src: trimmed, alt: imageAltDraft.trim() }).run();
  };

  return (
    <div className="flex-1 max-w-full">
      {label && <p className="text-sm font-extrabold">{label}:</p>}
      <p className="text-xs text-slate-600 mt-1 mb-2">{t("notifications.email_editor.email_compatibility_notice")}</p>
      <div className="inline-flex h-10 items-center rounded-md bg-stone-100 p-1 text-stone-600 mb-2">
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
            mode === "visual" ? "bg-white text-stone-900 shadow-sm" : "hover:text-stone-900"
          )}
          onClick={() => switchMode("visual")}
          disabled={isDisabled}
        >
          {t("notifications.email_editor.visual")}
        </button>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
            mode === "html" ? "bg-white text-stone-900 shadow-sm" : "hover:text-stone-900"
          )}
          onClick={() => switchMode("html")}
          disabled={isDisabled}
        >
          {t("notifications.email_editor.html_mode")}
        </button>
      </div>

      {mode === "visual" && editor && (
        <div className="email-html-editor-visual rounded border border-stone-200 bg-white">
          <div className="flex flex-wrap gap-1 border-b border-stone-200 px-2 py-1.5">
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive("bold") && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={isDisabled}
              aria-label="Bold"
            >
              <Bold className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive("italic") && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={isDisabled}
              aria-label="Italic"
            >
              <Italic className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="rounded p-1.5 hover:bg-stone-100"
              onClick={addOrEditLink}
              disabled={isDisabled}
              aria-label="Link"
            >
              <Link2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="rounded p-1.5 hover:bg-stone-100"
              onClick={openImageModal}
              disabled={isDisabled}
              aria-label={String(t("notifications.email_editor.image_aria"))}
            >
              <ImageIcon className="h-4 w-4" />
            </button>
            <span className="mx-0.5 w-px self-stretch bg-stone-200" aria-hidden />
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive({ textAlign: "left" }) && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              disabled={isDisabled}
              aria-label={String(t("notifications.email_editor.align_left"))}
            >
              <AlignLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive({ textAlign: "center" }) && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().setTextAlign("center").run()}
              disabled={isDisabled}
              aria-label={String(t("notifications.email_editor.align_center"))}
            >
              <AlignCenter className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive({ textAlign: "right" }) && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              disabled={isDisabled}
              aria-label={String(t("notifications.email_editor.align_right"))}
            >
              <AlignRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive({ textAlign: "justify" }) && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().setTextAlign("justify").run()}
              disabled={isDisabled}
              aria-label={String(t("notifications.email_editor.align_justify"))}
            >
              <AlignJustify className="h-4 w-4" />
            </button>
            <span className="mx-0.5 w-px self-stretch bg-stone-200" aria-hidden />
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive("bulletList") && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              disabled={isDisabled}
              aria-label="Bullet list"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={cn(
                "rounded p-1.5 hover:bg-stone-100",
                editor.isActive("orderedList") && "bg-stone-200"
              )}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              disabled={isDisabled}
              aria-label="Numbered list"
            >
              <ListOrdered className="h-4 w-4" />
            </button>
          </div>
          <EditorContent editor={editor} />
        </div>
      )}

      {mode === "html" && (
        <textarea
          rows={12}
          maxLength={maxLength}
          className="p-2 w-full border border-stone-200 rounded focus:outline-none font-mono text-sm"
          value={htmlDraft}
          onChange={(e) => onHtmlTextareaChange(e.target.value)}
          disabled={isDisabled}
          spellCheck={false}
        />
      )}

      <div className="flex justify-end text-xs mt-1">
        <span className="text-gray-500">
          {charCount}/{maxLength}
        </span>
      </div>

      <Dialog open={imageModalOpen} onOpenChange={(open) => !open && closeImageModal()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("notifications.email_editor.image_modal_title")}</DialogTitle>
            <DialogDescription>{t("notifications.email_editor.image_modal_description")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <label className="text-sm font-semibold text-stone-800" htmlFor="email-editor-image-url">
                {t("notifications.email_editor.image_url_prompt")}
              </label>
              <input
                id="email-editor-image-url"
                type="url"
                autoComplete="off"
                className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60"
                value={imageUrlDraft}
                onChange={(e) => {
                  setImageUrlDraft(e.target.value);
                  setImageUrlInvalid(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    insertImageFromModal();
                  }
                }}
                placeholder="https://"
              />
              {imageUrlInvalid && (
                <p className="mt-1 text-xs text-red-600">{t("notifications.email_editor.image_url_invalid")}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-stone-800" htmlFor="email-editor-image-alt">
                {t("notifications.email_editor.image_alt_prompt")}
              </label>
              <input
                id="email-editor-image-alt"
                type="text"
                autoComplete="off"
                className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60"
                value={imageAltDraft}
                onChange={(e) => setImageAltDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    insertImageFromModal();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <SecondaryButton text={String(t("global.cancel"))} fn={closeImageModal} />
            <PrimaryButton text={String(t("notifications.email_editor.image_modal_insert"))} fn={insertImageFromModal} />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
